import { call, put, select, spawn, take, takeEvery } from 'redux-saga/effects';

import logger from '@elab/core/utils/logger';
import constants, {
    adjustConfirmationDialog,
    adjustMaxAllowedProducts,
    enterPhoneNumberDialog,
} from '@elab/core/utils/constants';
import freeSamplesTypes from '@elab/components/connected/FreeSamples/redux/types';
import deliveryModesTypes from '@elab/components/connected/DeliveryModes/redux/types';
import { getCurrentLocation } from '@elab/core/utils/address';
import userSelectors from '@elab/core/store/user/selectors';
import isEmpty from '@elab/core/utils/isEmpty';
import analytics from '@elab/core/utils/analytics';

import {
    actions as globalActions,
    selectors as globalSelectors,
    types as globalTypes,
} from '../global';
import uiActions from '../ui/actions';
import uiTypes from '../ui/types';
import { actions as cartActions, selectors as cartSelectors, types as cartTypes } from '../cart';
import * as configSelectors from '../config/selectors';
import { getUserId } from '../../utils/user/sagaHelpers';
import cartApi from '../cart/api';
import authTypes from '../auth/types';

import deliveryAddressApi from './api';
import checkoutTypes from './types';
import actions from './actions';
import selectors from './selectors';

let DIALOG_NAME = adjustConfirmationDialog;
export function* goToFreeSamples({ payload }) {
    const target = payload.selector;
    yield put(globalActions.goto('/cart'));
    yield take(freeSamplesTypes.SET_PRODUCTS);
    yield put(uiActions.scrollToTop({ target }));
}

export function* handleUseDeliveryAsBillingSetter() {
    const deliveryAddress = yield select(cartSelectors.getDeliveryAddress);
    const paymentAddress = yield select(cartSelectors.getPaymentAddress);
    yield put(actions.setAddressForBilling(null));
    yield put(
        actions.setUseDeliveryAsBilling({
            value: deliveryAddress.id === paymentAddress.id,
        }),
    );
}

export function* setAddressForBilling({ payload }) {
    let address;
    // [ ] use delivery as billing = false
    if (!payload.value) {
        const cartDelivery = yield select(cartSelectors.getDeliveryAddress);
        const cartBilling = yield select(cartSelectors.getPaymentAddress);
        const temp = yield select(cartSelectors.getTemporaryAddress);
        const isSameAddress = cartDelivery.id === cartBilling.id;

        address = isSameAddress ? temp || { empty: true } : cartBilling;
        yield put(cartActions.setTemporaryAddress(cartBilling));

        yield put(actions.setUseDeliveryAsBilling({ value: false }));

        if (isSameAddress) {
            yield put(cartActions.removeBillingAddress());
        }
    } else {
        // [x] use delivery as billing = true
        address = yield select(cartSelectors.getDeliveryAddress);
        yield put(actions.setUseDeliveryAsBilling({ value: true }));
    }
    yield put(actions.setAddressForBilling(address));
}

export function* savePhoneNumber({ payload }) {
    const phoneNumber = yield select(cartSelectors.getPhoneNumber);
    const phoneNumberIsNotValid = yield select(cartSelectors.getPhoneNumberIsNotValid);
    if (!phoneNumberIsNotValid) {
        const userId = yield call(getUserId);
        const cartId = yield select(cartSelectors.getCartId);
        try {
            yield call(cartApi.putPhoneNumber, userId, cartId, phoneNumber);
            yield put(cartActions.setDataAttr('phoneNumberSaved', true));
            yield call(onCheckRedirect, { payload });
        } catch (e) {
            yield call(logger.error, e);
        }
    }
}

export function* onCheckRedirect({ payload }) {
    if (payload.loadCart) {
        yield put(cartActions.loadCart());
        yield take(cartTypes.SET_LOADED);
    }
    const addressForBilling = yield select(selectors.getAddressForBilling);
    const paymentAddress = yield select(cartSelectors.getPaymentAddress);
    const deliveryAddress = yield select(cartSelectors.getDeliveryAddress);
    const deliveryMode = yield select(cartSelectors.getDeliveryMode);
    const isPickup =
        deliveryMode.type &&
        (deliveryMode.type.startsWith('PICKUP') || deliveryMode.type === 'POST_POINT');
    const isPickupExpress = deliveryMode.type && deliveryMode.type == 'PICKUP_EXPRESS';
    const pickupExpressEnterPhoneEnabled = yield select(selectors.isPickupExpressEnterPhoneEnabled);

    if (
        (!deliveryAddress ||
            !deliveryAddress.id ||
            (addressForBilling && addressForBilling.empty) ||
            (!addressForBilling && !paymentAddress.id)) &&
        !isPickup
    ) {
        yield put(actions.emptyDeliveryAddress(true));
        yield put(uiActions.scrollToCenter());
        return;
    }

    if (addressForBilling) yield call(putAddressForBilling, addressForBilling);
    yield put(actions.emptyDeliveryAddress(false));

    const { pathname = '' } = yield call(getCurrentLocation);

    const pickupExpressEnterPhonePage = yield select(
        configSelectors.getPickupExpressEnterPhonePage,
    );

    const haveOutOfStock = yield select(cartSelectors.getHaveOutOfStock);
    const skipAutoAdjustmentOnPages = yield select(configSelectors.getSkipAutoAdjustmentOnPages);
    let preferredLanguage = yield select(globalSelectors.getPreferredLanguage);

    if (
        isPickupExpress &&
        pickupExpressEnterPhoneEnabled &&
        pathname.includes(pickupExpressEnterPhonePage)
    ) {
        const phoneNumberSaved = yield select(cartSelectors.getPhoneNumberSaved);
        if (phoneNumberSaved) {
            yield put(uiActions.closeDialogByName({ name: enterPhoneNumberDialog }));
            yield put(cartActions.setDataAttr('phoneNumberSaved', false));
        } else {
            yield put(uiActions.openDialog({ name: enterPhoneNumberDialog }));
            return;
        }
    }
    if (!preferredLanguage && payload.langIsoCode) {
        const splitedLanguage = payload.langIsoCode.split('_');
        preferredLanguage = splitedLanguage.length > 0 ? splitedLanguage[0] : preferredLanguage;
    }
    const pathnameWithoutLanguage =
        preferredLanguage && pathname.startsWith(`/${preferredLanguage}`)
            ? pathname.substring(preferredLanguage.length + 1)
            : pathname;
    const shouldRedirect = yield call(checkForMaxAllowedQuantityForDelivery);
    if (
        haveOutOfStock &&
        skipAutoAdjustmentOnPages.includes(pathnameWithoutLanguage) &&
        DIALOG_NAME === adjustConfirmationDialog
    ) {
        yield put(uiActions.openDialog({ name: DIALOG_NAME }));
        const action = yield take([cartTypes.ADJUST_CART, uiTypes.CLOSE_DIALOG]);

        if (action.type === uiTypes.CLOSE_DIALOG) return;
    }

    if ((!haveOutOfStock || payload.allowRedirection) && shouldRedirect) {
        yield put(cartActions.checkRedirect(payload));
    }
}

function* checkForMaxAllowedQuantityForDelivery() {
    const { code } = yield select(cartSelectors.getDeliveryMode);
    const pointOfService = yield select(cartSelectors.getDeliveryPointOfService);
    const cartItems = yield select(cartSelectors.getEntries);
    const maxQuantity = yield select(cartSelectors.maxAllowedProductQuantity);
    const outOfStockEntries = yield select(cartSelectors.getOutOfStockEntries);
    let hasOutOfStock = !!outOfStockEntries.length;
    const configDialogName = yield select(configSelectors.getAdjustDialogName);
    DIALOG_NAME =
        configDialogName === adjustMaxAllowedProducts ? adjustMaxAllowedProducts : DIALOG_NAME;
    if (
        (maxQuantity || hasOutOfStock) &&
        (!code.includes(constants.ccDeliveryName) ||
            (code.includes(constants.ccDeliveryName) && pointOfService.pickupExpressEligible))
    ) {
        yield put(cartActions.setHaveOutOfStock(hasOutOfStock));
        const hasProductQuantityThanMaxAllowed =
            cartItems.length && cartItems.some(({ quantity }) => quantity > maxQuantity);
        if (hasProductQuantityThanMaxAllowed || hasOutOfStock) {
            yield put(uiActions.openDialog({ name: DIALOG_NAME }));
            const action = yield take([cartTypes.ADJUST_CART, uiTypes.CLOSE_DIALOG]);

            return action.type === cartTypes.ADJUST_CART;
        }
    }
    return true;
}

export function* putAddressForBilling(addressForBilling) {
    const userId = yield call(getUserId);
    const cartId = yield select(cartSelectors.getCartId);
    try {
        yield call(cartApi.putBillingAddress, userId, cartId, addressForBilling.id);
    } catch (e) {
        yield call(logger.error, e);
    }
}

export function* changeDeliveryAddress() {
    yield put(actions.setChangeDeliveryAddress({ status: true }));
    yield put(cartActions.clearDeliveryAddress());
}

export function* hideDeliveryAddress() {
    yield put(actions.hideDeliveryAddress(true));
    yield take(deliveryModesTypes.SET_DELIVERY_MODE);
    yield put(actions.hideDeliveryAddress(false));
}

export function* deliveryModeOnLoginDone() {
    yield take(cartTypes.SET_CART_DATA);
    const currentDeliveryMode = yield select(selectors.getDeliveryMode);
    const usePostPointAddressAfterLogin = yield select(
        configSelectors.usePostPointAddressAfterLogin,
    );

    if (
        currentDeliveryMode &&
        currentDeliveryMode.includes(constants.ppDeliveryType) &&
        !usePostPointAddressAfterLogin
    ) {
        try {
            const cartId = yield select(cartSelectors.getCartId);
            const userId = yield select(userSelectors.getUserId);

            yield call(deliveryAddressApi.deleteDeliveryAddress, userId, cartId);
            yield put(cartActions.clearDeliveryAddress());
        } catch (e) {
            yield call(logger.error, e);
        }
    }
}

export function* onShippingCountdownEnded() {
    yield put(actions.closeShippingCountdown());
    yield put(uiActions.openDialog({ name: 'expired-shipping-popup' }));
}

export function* onDeliveryModeRedirect({ payload }) {
    yield put(uiActions.closeDialogByName('expired-shipping-popup'));
    yield put(globalActions.redirect(payload.successRedirectUrl));
}

export function* changeDeliveryAddressAndRedirect({ payload }) {
    yield put(globalActions.redirect(payload.successRedirectUrl));
    yield take(globalTypes.PAGE_LOAD);
    yield put(actions.setChangeDeliveryAddress({ status: true }));
    yield put(cartActions.clearDeliveryAddress());
}

export function* checkDeliveryAddress() {
    const cartData = yield select(cartSelectors.getData);

    if (isEmpty(cartData)) {
        yield take(cartTypes.SET_CART_DATA);
    }
    const deliveryModeCode = yield select(cartSelectors.getDeliveryModeCode);
    const deliveryAddress = yield select(cartSelectors.getDeliveryAddress);

    if (
        (deliveryModeCode && deliveryAddress.id) ||
        deliveryModeCode === constants.deliveryModeTypeEmail
    ) {
        yield put(actions.setChangeDeliveryAddress({ status: false }));
    }
}

export function* onPaymentRedirect({ payload }) {
    yield put(globalActions.redirect(payload.successRedirectUrl));
    const cartData = yield select(cartSelectors.getData);
    yield spawn(analytics.handleGiftOptionConfirmEvent, cartData);
}

export default [
    takeEvery(checkoutTypes.GO_TO_FREE_SAMPLES, goToFreeSamples),
    takeEvery(checkoutTypes.SAGA_SET_ADDRESS_FOR_BILLING, setAddressForBilling),
    takeEvery(checkoutTypes.CHECK_REDIRECT, onCheckRedirect),
    takeEvery(checkoutTypes.CHANGE_DELIVERY_ADDRESS, changeDeliveryAddress),
    takeEvery(checkoutTypes.CLEAR_DELIVERY_MODE_ON_PAYMENT_STEP, hideDeliveryAddress),
    takeEvery(authTypes.LOGIN_DONE, deliveryModeOnLoginDone),
    takeEvery(checkoutTypes.SHIPPING_COUNTDOWN_ENDED, onShippingCountdownEnded),
    takeEvery(checkoutTypes.DELIVERY_MODE_REDIRECT, onDeliveryModeRedirect),
    takeEvery(checkoutTypes.SAVE_PHONE_NUMBER, savePhoneNumber),
    takeEvery(checkoutTypes.CHANGE_DELIVERY_ADDRESS_AND_REDIRECT, changeDeliveryAddressAndRedirect),
    takeEvery(checkoutTypes.CHECK_DELIVERY_ADDRESS, checkDeliveryAddress),
    takeEvery(checkoutTypes.PAYMENT_REDIRECT, onPaymentRedirect),
];
